import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiUrl } from "@/lib/api-client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { 
  Sparkles, 
  Loader2, 
  CheckCircle2, 
  XCircle, 
  ExternalLink, 
  RefreshCw,
  FileText,
  Download,
  AlertCircle
} from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "@/hooks/use-toast";

interface QuoteAdvanceGeneratorProps {
  dealId: string;
  quoteDocument: {
    id: string;
    document_type: string;
    document_number?: string;
    document_name?: string;
    total_amount?: number;
    currency?: string;
  };
  existingDepositInvoice?: {
    id: string;
    document_number?: string;
    pdf_url?: string;
    total_amount?: number;
    currency?: string;
  };
  existingContract?: {
    id: string;
    template_id?: string;
    pdf_url?: string;
    document_number?: string;
    document_name?: string;
  };
}

export function QuoteAdvanceGenerator({
  dealId,
  quoteDocument,
  existingDepositInvoice,
  existingContract,
}: QuoteAdvanceGeneratorProps) {
  const { t } = useTranslation();
  const quoteTotal = quoteDocument.total_amount || 0;
  const currency = quoteDocument.currency || "EUR";

  const [percentage, setPercentage] = useState(70);
  const [amountInput, setAmountInput] = useState<string>("");
  const [sendByEmail, setSendByEmail] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const qc = useQueryClient();

  // Derived: deposit amount from percentage, or use amountInput when valid
  const depositAmountFromPercent = (quoteTotal * percentage) / 100;
  const amountNum = parseFloat(amountInput.replace(",", "."));
  const isAmountValid = !isNaN(amountNum) && amountNum > 0 && amountNum <= quoteTotal;
  const effectiveDepositAmount = isAmountValid ? amountNum : depositAmountFromPercent;
  const effectivePercentage = quoteTotal > 0 ? (effectiveDepositAmount / quoteTotal) * 100 : percentage;

  // Only show for quotes
  if (quoteDocument.document_type !== "quote") {
    return null;
  }

  const generateAdvance = useMutation({
    mutationFn: async (params: { percentage?: number; amount?: number; sendByEmail?: boolean }) => {
      const body: Record<string, unknown> = { templateId: "zmluva-o-dielo", sendByEmail: sendByEmail };
      if (params.amount != null && params.amount > 0) {
        body.amount = params.amount;
      } else {
        body.percentage = params.percentage ?? 70;
      }
      const response = await fetch(
        apiUrl(`/api/crm/deals/${dealId}/documents/${quoteDocument.id}/generate-advance`),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify(body),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
      }

      return response.json();
    },
    onSuccess: (data) => {
      const message = data.emailSent
        ? t("quoteExtra.advanceSuccess")
        : t("quoteExtra.advanceSuccessNoEmail");
      toast({
        title: t("common.success"),
        description: message,
      });
      if (!data.emailSent && data.emailError) {
        toast({
          title: t("quoteExtra.advanceEmailErrorTitle", "E-mail hiba"),
          description: data.emailError,
          variant: "destructive",
        });
      }
      if (data.emailAttachmentWarning) {
        toast({
          title: t("quoteExtra.attachmentWarningTitle", "Upozornenie na prílohu"),
          description: data.emailAttachmentWarning,
          variant: "destructive",
        });
      }
      setError(null);
      // Invalidate queries to refresh document list
      qc.invalidateQueries({ queryKey: ["deal-documents", dealId] });
      qc.invalidateQueries({ queryKey: ["deal", dealId] });
      qc.invalidateQueries({ queryKey: ["deal-contracts", dealId] });
    },
    onError: (error: Error) => {
      const errorMessage = error.message || t("quoteExtra.unknownError");
      setError(errorMessage);
      toast({
        title: t("common.error"),
        description: errorMessage,
        variant: "destructive",
      });
    },
  });

  const handleGenerate = () => {
    if (isAmountValid) {
      if (effectiveDepositAmount <= 0 || effectiveDepositAmount > quoteTotal) {
        setError("Az előleg összege 0 és az árajánlat értéke között kell legyen");
        return;
      }
    } else {
      if (percentage <= 0 || percentage > 100) {
        setError("A százalék 1 és 100 között kell legyen");
        return;
      }
    }
    setError(null);
    generateAdvance.mutate(
      isAmountValid ? { amount: effectiveDepositAmount } : { percentage }
    );
  };

  const formatAmount = (amount: number) => {
    return `${amount.toFixed(2).replace('.', ',')} ${currency}`;
  };

  return (
    <Card className="mt-4 border-l-4 border-l-primary">
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-primary" />
          <CardTitle className="text-sm font-semibold">
            Előlegszámla és szerződés generálás
          </CardTitle>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Error Alert */}
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>{t("common.error")}</AlertTitle>
            <AlertDescription className="mt-2">
              <div className="space-y-1">
                <p>{error}</p>
                <div className="text-xs mt-2 space-y-1">
                  <p className="font-semibold">{t("quoteExtra.possibleSolutions")}</p>
                  <ul className="list-disc list-inside space-y-1 ml-2">
                    <li>{t("quoteExtra.checkSuperfaktura")}</li>
                    <li>{t("quoteExtra.checkTemplate")}</li>
                    <li>{t("quoteExtra.checkNetwork")}</li>
                    <li>{t("quoteExtra.tryAgainLater")}</li>
                  </ul>
                </div>
              </div>
            </AlertDescription>
          </Alert>
        )}

        {/* Generation Form */}
        {!existingDepositInvoice && !existingContract && (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="percentage">{t("quoteExtra.advancePercentLabel")}</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="percentage"
                  type="number"
                  min="1"
                  max="100"
                  step="0.1"
                  value={isAmountValid ? Math.round(effectivePercentage * 10) / 10 : percentage}
                  onChange={(e) => {
                    const value = parseFloat(e.target.value);
                    if (!isNaN(value) && value >= 1 && value <= 100) {
                      setPercentage(value);
                      setAmountInput("");
                    }
                  }}
                  className="w-24"
                  disabled={generateAdvance.isPending}
                />
                <span className="text-sm text-foreground/60">%</span>
                <div className="flex-1 text-sm text-foreground/60">
                  = {formatAmount(effectiveDepositAmount)}
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="amount">{t("quoteExtra.advanceAmountLabel")}</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="amount"
                  type="text"
                  inputMode="decimal"
                  placeholder="pl. 1044,55"
                  value={amountInput}
                  onChange={(e) => {
                    setAmountInput(e.target.value);
                  }}
                  className="w-32"
                  disabled={generateAdvance.isPending}
                />
                <span className="text-sm text-foreground/60">{currency}</span>
                {amountInput && (
                  <div className="flex-1 text-sm text-foreground/60">
                    {isAmountValid
                      ? `≈ ${Math.round(effectivePercentage * 10) / 10}%`
                      : amountInput.trim() ? t("quoteExtra.advanceAmountInvalid") : ""}
                  </div>
                )}
              </div>
            </div>

            <p className="text-xs text-foreground/60">
              Az árajánlat értéke: {formatAmount(quoteTotal)}
            </p>

            <div className="flex items-center gap-2">
              <Checkbox
                id="send-by-email"
                checked={sendByEmail}
                onCheckedChange={(checked) => setSendByEmail(checked === true)}
                disabled={generateAdvance.isPending}
              />
              <Label htmlFor="send-by-email" className="text-sm font-normal cursor-pointer">
                {t("quoteExtra.sendByEmailLabel")}
              </Label>
            </div>

            <Button
              onClick={handleGenerate}
              disabled={
                generateAdvance.isPending ||
                (isAmountValid ? effectiveDepositAmount <= 0 || effectiveDepositAmount > quoteTotal : percentage <= 0 || percentage > 100)
              }
              className="w-full"
            >
              {generateAdvance.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Generálás...
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4 mr-2" />
                  Előlegszámla és szerződés generálása
                </>
              )}
            </Button>
          </div>
        )}

        {/* Existing Documents */}
        {(existingDepositInvoice || existingContract) && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm text-foreground/60">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <span>{t("quoteExtra.generatedDocuments")}</span>
            </div>

            {/* Deposit Invoice */}
            {existingDepositInvoice && (
              <div className="p-3 bg-muted rounded-lg border">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">
                        Előlegszámla
                      </Badge>
                      {existingDepositInvoice.document_number && (
                        <span className="text-sm font-medium">
                          {existingDepositInvoice.document_number}
                        </span>
                      )}
                    </div>
                    {existingDepositInvoice.total_amount !== undefined && (
                      <p className="text-sm text-foreground/60">
                        Összeg: {formatAmount(existingDepositInvoice.total_amount)}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    {existingDepositInvoice.pdf_url && (
                      <Button
                        variant="ghost"
                        size="sm"
                        asChild
                        className="h-8 w-8 p-0"
                      >
                        <a
                          href={existingDepositInvoice.pdf_url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <ExternalLink className="h-4 w-4" />
                        </a>
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setError(null);
                        generateAdvance.mutate(
                          isAmountValid ? { amount: effectiveDepositAmount } : { percentage }
                        );
                      }}
                      disabled={generateAdvance.isPending}
                      className="h-8 w-8 p-0"
                      title={t("quoteExtra.regenerateTitle")}
                    >
                      <RefreshCw className={`h-4 w-4 ${generateAdvance.isPending ? "animate-spin" : ""}`} />
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Contract */}
            {existingContract && (
              <div className="p-3 bg-muted rounded-lg border">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge variant="secondary" className="bg-blue-100 text-blue-800">
                        Szerződés
                      </Badge>
                      {existingContract.document_number && (
                        <span className="text-sm font-medium">
                          {existingContract.document_number}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-foreground/60">
                      {existingContract.document_name || t("quoteExtra.contractFallback")}
                    </p>
                  </div>
                  <div className="flex items-center gap-1">
                    {existingContract.pdf_url && (
                      <Button
                        variant="ghost"
                        size="sm"
                        asChild
                        className="h-8 w-8 p-0"
                        title={t("quoteExtra.viewTitle")}
                      >
                        <a
                          href={existingContract.pdf_url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <ExternalLink className="h-4 w-4" />
                        </a>
                      </Button>
                    )}
                    {existingContract.pdf_url && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          window.open(existingContract.pdf_url, '_blank');
                        }}
                        className="h-8 w-8 p-0"
                        title={t("quoteExtra.downloadTitle")}
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setError(null);
                        generateAdvance.mutate(
                          isAmountValid ? { amount: effectiveDepositAmount } : { percentage }
                        );
                      }}
                      disabled={generateAdvance.isPending}
                      className="h-8 w-8 p-0"
                      title={t("quoteExtra.regenerateTitle")}
                    >
                      <RefreshCw className={`h-4 w-4 ${generateAdvance.isPending ? "animate-spin" : ""}`} />
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Regenerate Button */}
            <div className="pt-2 border-t space-y-3">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="send-by-email-regenerate"
                  checked={sendByEmail}
                  onCheckedChange={(checked) => setSendByEmail(checked === true)}
                  disabled={generateAdvance.isPending}
                />
                <Label htmlFor="send-by-email-regenerate" className="text-sm font-normal cursor-pointer">
                  {t("quoteExtra.sendByEmailLabel")}
                </Label>
              </div>
              <div className="space-y-2">
                <Label htmlFor="percentage-regenerate">Előleg százaléka (újrageneráláshoz)</Label>
                <div className="flex items-center gap-2 flex-wrap">
                  <Input
                    id="percentage-regenerate"
                    type="number"
                    min="1"
                    max="100"
                    step="0.1"
                    value={isAmountValid ? Math.round(effectivePercentage * 10) / 10 : percentage}
                    onChange={(e) => {
                      const value = parseFloat(e.target.value);
                      if (!isNaN(value) && value >= 1 && value <= 100) {
                        setPercentage(value);
                        setAmountInput("");
                      }
                    }}
                    className="w-24"
                    disabled={generateAdvance.isPending}
                  />
                  <span className="text-sm text-foreground/60">%</span>
                  <span className="text-sm text-foreground/60">= {formatAmount(effectiveDepositAmount)}</span>
                  <div className="flex items-center gap-2">
                    <Input
                      type="text"
                      inputMode="decimal"
                      placeholder="pl. 1044,55"
                      value={amountInput}
                      onChange={(e) => setAmountInput(e.target.value)}
                      className="w-28"
                      disabled={generateAdvance.isPending}
                    />
                    <span className="text-sm text-foreground/60">{currency}</span>
                    {amountInput && isAmountValid && (
                      <span className="text-sm text-foreground/60">≈ {Math.round(effectivePercentage * 10) / 10}%</span>
                    )}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setError(null);
                      generateAdvance.mutate(
                        isAmountValid ? { amount: effectiveDepositAmount } : { percentage }
                      );
                    }}
                    disabled={
                      generateAdvance.isPending ||
                      (isAmountValid ? effectiveDepositAmount <= 0 || effectiveDepositAmount > quoteTotal : percentage <= 0 || percentage > 100)
                    }
                  >
                    {generateAdvance.isPending ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                        {t("quoteExtra.regenerateTitle")}...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="h-4 w-4 mr-1" />
                        {t("quoteExtra.regenerateTitle")}
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

